<!--
  Copyright (C) 2023 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <PrimeDialog
    v-if="isSelectedNodeASite"
    :header="$t(`members.management`, { workspace: workspaceTitle })"
    visible
    modal
    maximizable
    class="popup popup--tree"
    @keyup.esc="closePopup(true)"
  >
    <template #closeicon>
      <PrimeButton
        icon="ri-close-line"
        :aria-label="$t('close')"
        outlined
        text
        severity="secondary"
        autofocus="false"
        @click="closePopup(true)"
      />
    </template>
    <MemberList
      :workspace="workspace"
      :members="members"
      :permissions="enablePermissions"
      :role="role"
      @reload="loadMembers()"
    />
    <template #footer>
      <PrimeButton
        :label="$t('close')"
        severity="secondary"
        @click="closePopup(true)"
      />
    </template>
  </PrimeDialog>
  <PrimeDialog
    v-else
    :header="$t(`members.permissions`, { node: nodeName })"
    visible
    modal
    maximizable
    class="popup popup--tree"
    @keyup.esc="closePopup(true)"
  >
    <template #closeicon>
      <PrimeButton
        icon="ri-close-line"
        :aria-label="$t('close')"
        outlined
        text
        severity="secondary"
        autofocus="false"
        @click="closePopup(true)"
      />
    </template>
    <div class="member-popup__site-link">
      <PrimeButton
        :label="$t('membersPopup.goToSiteMemberPage')"
        @click="onGoToSiteMemberPage"
      />
    </div>
    <MemberList
      :workspace="workspace"
      :folder-node="selectedNode"
      :members="membersWithGroups"
      :permissions="enablePermissions"
      :role="role"
      :is-site="isSelectedNodeASite"
      :is-herited="isHerited"
      :nb-members="nbMembers"
      :members-without-groups="members"
      :members-locally-set="membersLocallySet"
      @update-permission="loadPermissions()"
    />
    <template #footer>
      <PrimeButton
        :label="$t('close')"
        severity="secondary"
        @click="closePopup(true)"
      />
    </template>
  </PrimeDialog>
</template>

<script>
import MemberList from "../users/MemberList.vue";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";
import workspaceService from "@/services/WorkspaceService.js";
import { useNavigationStore } from "@/stores/navigation.js";
import { groupsApi } from "@/AlfrescoApi.js";

import Dialog from "primevue/dialog";
import Button from "primevue/button";

export default {
  name: "MemberListPopup",
  components: {
    MemberList,
    PrimeDialog: Dialog,
    PrimeButton: Button,
  },
  props: {
    selectedNode: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["close", "goToSiteMemberPage"],
  data() {
    return {
      workspace: null,
      node: null,
      nodeName: null,
      workspaceTitle: "",
      members: null,
      enablePermissions: false,
      role: null,
      isHerited: true,
      nbMembers: null,
      membersWithGroups: null,
      membersLocallySet: null,
    };
  },
  computed: {
    isSelectedNodeASite() {
      return this.selectedNode.nodeType === "st:site";
    },
  },
  mounted() {
    if (this.isSelectedNodeASite) {
      // Use selectedNode.name which contains the site shortName
      const siteId = this.selectedNode.name;
      workspaceService
        .getWorkspace(siteId)
        .then((workspace) => {
          this.workspace = workspace;
          this.workspaceTitle = workspace.title;
          return workspace;
        })
        .then((workspace) => {
          this.role = this.$t(`role.${workspace.role}`);
          this.enablePermissions = this.role === this.$t("role.SiteManager");
        });
      this.loadMembers(siteId);
    } else {
      alfrescoNodeService.getNode(this.selectedNode.id).then((node) => {
        this.node = node;
        this.nodeName = node.name;
        this.loadPermissions();
      });
    }
  },
  methods: {
    onGoToSiteMemberPage() {
      const navigationStore = useNavigationStore();
      this.$emit("goToSiteMemberPage", navigationStore.workspace?.id);
      this.closePopup(false);
    },
    closePopup(reload) {
      this.$emit("close", reload);
    },
    loadMembers(siteId) {
      let opts = {
        maxItems: 1000,
      };
      let users = workspaceService
        .getWorkspaceMembershipsWithOpts(siteId, opts)
        .then((usersArr) => {
          return usersArr.list.entries.map((user) => ({
            ...user.entry,
            isGroup: false,
          }));
        });
      let groups = workspaceService
        .getWorkspaceGroupsWithOpts(siteId, opts)
        .then((groupsArr) => {
          return groupsArr.map((group) => ({
            ...group,
            isGroup: true,
          }));
        });
      Promise.all([users, groups]).then(
        (result) => (this.members = [...result[0], ...result[1]]),
      );
    },
    updateInheritedGroup(node) {
      // Use workspace id from navigation store instead of route params
      const siteId = useNavigationStore().workspace?.id;
      const authorityIdToRemove = `GROUP_site_${siteId}_SiteContributor`;
      return (node.permissions?.inherited || []).filter(
        (item) => item.authorityId !== authorityIdToRemove,
      );
    },
    fetchGroupMemberships() {
      // Filter out special groups that don't support listGroupMemberships API
      const excludedGroups = ["GROUP_EVERYONE"];
      const groups = this.membersWithGroups.filter(
        (obj) =>
          obj.authorityId.startsWith("GROUP") &&
          !excludedGroups.includes(obj.authorityId),
      );
      groups.forEach((group) => {
        groupsApi.listGroupMemberships(group.authorityId).then((users) => {
          let usersList = users.list.entries;
          usersList.forEach((user) => {
            let userObject = user.entry;
            userObject.groupId = group.authorityId;
            userObject.authorityId = userObject.id;
            userObject.name = group.name;
            this.membersWithGroups.push(userObject);
          });
        });
      });
    },
    loadPermissions() {
      alfrescoNodeService.getNode(this.selectedNode.id).then((node) => {
        this.enablePermissions =
          useNavigationStore().currentNode.allowableOperations?.includes(
            "updatePermissions",
          ) || false;
        this.isHerited = node.permissions?.isInheritanceEnabled;
        this.members = node.permissions?.locallySet || [];
        this.membersLocallySet = node.permissions?.locallySet || [];
        let inheritedGroup = this.updateInheritedGroup(node);
        this.nbMembers = this.members.length + inheritedGroup.length;
        this.membersWithGroups = [...this.members, ...inheritedGroup];
        this.fetchGroupMemberships();
      });
    },
  },
};
</script>

<style lang="css" scoped>
.member-popup__site-link {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.75rem;
}
</style>
